import { EventColorPalette } from "@/config";
import { BLUE, MINT, SALMON, VIOLET } from "@/config/mui/colorPalette";
import { CalendarViews } from "@/hooks/appointments/useCalendarTypeView";
import { AppointmentStatus } from "@/types";

export const getColorForStatus = (status: AppointmentStatus) =>
  ({
    [AppointmentStatus.SCHEDULED]: BLUE[80],
    [AppointmentStatus.CONFIRMED]: VIOLET[80],
    [AppointmentStatus.COMPLETED]: MINT[80],
    [AppointmentStatus.CANCELLED]: SALMON[80],
    [AppointmentStatus.CANCELLED_LATE]: SALMON[80],
    [AppointmentStatus.NO_SHOW]: SALMON[80],
  })[status];

export const getIconColors = (
  status: AppointmentStatus,
  eventColorPalette: EventColorPalette,
  iconColors?: string,
  appointmentIconColors?: string
) => {
  const fallbackColor = getColorForStatus(status);

  const deprecatedColors =
    status === AppointmentStatus.COMPLETED
      ? "currentColor"
      : eventColorPalette?.[80] ?? fallbackColor;

  return {
    iconColor: iconColors || deprecatedColors,
    appointmentIconColor: appointmentIconColors || deprecatedColors,
  };
};

type AppointmentRules = {
  showIcons: boolean;
  showChips: boolean;
  showTime: boolean;
  showAvatars: boolean;
  sameLineTitle: boolean;
};

export const getAppointmentTileRules = (options: {
  duration: number;
  isMobile: boolean;
  view: CalendarViews;
  isNarrowStack: boolean;
  isMediumStack: boolean;
}): AppointmentRules => {
  const {
    duration,
    isMobile,
    view = CalendarViews.DAY,
    isNarrowStack,
    isMediumStack,
  } = options;

  const dayMobileView = view === CalendarViews.DAY && isMobile;
  const weekMobileView = view === CalendarViews.WEEK && isMobile;
  const dayDesktopView = view === CalendarViews.DAY && !isMobile;
  const weekDesktopView = view === CalendarViews.WEEK && !isMobile;

  if (dayMobileView) {
    if (duration >= 90) {
      return {
        showAvatars: !isNarrowStack,
        showTime: !isNarrowStack,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 60) {
      return {
        showAvatars: !isNarrowStack,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 45) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: !isNarrowStack,
      };
    }

    if (duration >= 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: !isNarrowStack,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration < 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: !isNarrowStack,
        showChips: false,
        sameLineTitle: true,
      };
    }
  }

  if (weekMobileView) {
    if (duration >= 90) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 60) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 45) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: false,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: false,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration < 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: false,
        showChips: false,
        sameLineTitle: true,
      };
    }
  }

  if (dayDesktopView) {
    if (duration >= 90) {
      return {
        showAvatars: !isNarrowStack,
        showTime: true,
        showIcons: true,
        showChips: true,
        sameLineTitle: false,
      };
    }

    if (duration >= 60) {
      return {
        showAvatars: !isNarrowStack,
        showTime: false,
        showIcons: true,
        showChips: true,
        sameLineTitle: false,
      };
    }

    if (duration >= 45) {
      return {
        showAvatars: !isNarrowStack && !isMediumStack,
        showTime: false,
        showIcons: true,
        showChips: true,
        sameLineTitle: true,
      };
    }

    if (duration >= 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: !isMediumStack && !isNarrowStack,
        sameLineTitle: false,
      };
    }

    if (duration < 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: !isMediumStack && !isNarrowStack,
        sameLineTitle: true,
      };
    }
  }

  if (weekDesktopView) {
    if (duration >= 90) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 60) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 45) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: true,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration >= 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: false,
        showChips: false,
        sameLineTitle: false,
      };
    }

    if (duration < 30) {
      return {
        showAvatars: false,
        showTime: false,
        showIcons: false,
        showChips: false,
        sameLineTitle: true,
      };
    }
  }

  // This should never be reached if all cases are handled
  throw new Error(`Unhandled view type: ${view}, isMobile: ${isMobile}`);
};
